import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';

// Import only the types that don't create circular dependencies
import type { ApiEndpoint, MediaItemDetailed } from '../types';
import { fetchItemWithRelationships, getRelatedMedia, getDataSourceType } from '../utilities/dataSourceAdapters';
import { useConfig } from './ConfigContext';
import { useUser, registerContextCleanupHandler, unregisterContextCleanupHandler } from './UserContext';
// CloudFront auth validation removed - not needed for normal operation

// Define a more generic Entity type that can represent ANY entity
export interface Entity {
  id: string | number;
  name: string;
  entityType: string;
  layerId?: string;
  coordinates?: [number, number];
  geometry?: any;
  properties?: Record<string, any>;
  relatedMedia?: MediaItemDetailed[];
  hasMedia?: boolean;
  relationships?: any; // Complete relationships object from API
  isComplete?: boolean; // Flag to indicate if full data has been fetched
  // Additional fields that can be at the top level
  thumbnail?: string;
  description?: string;
  primary_image?: any;
  created_at?: string;
  last_updated_at?: string;
  url?: string; // For media items
}

// Keep GeoEntity as an alias for backward compatibility
export type GeoEntity = Entity;

// Define types for different filter operations
type FilterType = "intersects" | "contains" | "equals";

interface Filter {
  type: FilterType;
  db_table: string;
  field: string;
  value: string | null;
}

// Define the context type locally
export interface SelectionContextType {
  // Legacy props (maintained for backward compatibility)
  filterArea: string | null;
  setFilterArea: (value: string | null, showFilteredLayers?: boolean) => void;
  currentFilter: Filter;
  filterLayersVisible: boolean;
  setFilterLayersVisible: (visible: boolean) => void;
  
  // New structured props - renamed to be more generic
  selectedPrimaryFilterEntity: Entity | null;
  setSelectedPrimaryFilterEntity: (entity: Entity | null, showFilteredLayers?: boolean) => void;
  selectedEntity: Entity | null; // Renamed from selectedGeoEntity
  setSelectedEntity: (entity: Entity | null, endpoint?: ApiEndpoint) => void; // Enhanced to accept endpoint
  updateSelectedEntityWithMedia: (entity: Entity, mediaItems?: MediaItemDetailed[], endpoint?: ApiEndpoint) => void;
  
  // NEW: Selected endpoint tracking
  selectedEndpoint: ApiEndpoint | null; // The endpoint associated with the selected entity
  setSelectedEndpoint: (endpoint: ApiEndpoint | null) => void;
  
  // Cache invalidation methods
  invalidateEntityCache: (entityId: string | number, entityType: string) => void;
  refreshSelectedEntity: () => Promise<void>;
  fetchRelationshipsForSelected: () => Promise<boolean>;
  
  // Loading progress
  loadingProgress: {
    step: string;
    current: number;
    total: number;
  } | null;
  
  // Backward compatibility aliases
  selectedGeoEntity: Entity | null;
  setSelectedGeoEntity: (entity: Entity | null, endpoint?: ApiEndpoint) => void; // Enhanced for backward compatibility
}

// Create context with default values
const SelectionContext = React.createContext<SelectionContextType>({
  filterArea: null,
  setFilterArea: () => {},
  currentFilter: {
    type: "intersects",
    db_table: "FamilyArea",
    field: "name",
    value: "",
  },
  filterLayersVisible: false,
  setFilterLayersVisible: () => {},
  selectedPrimaryFilterEntity: null,
  setSelectedPrimaryFilterEntity: () => {},
  selectedEntity: null,
  setSelectedEntity: () => {},
  updateSelectedEntityWithMedia: () => {},
  selectedEndpoint: null,
  setSelectedEndpoint: () => {},
  invalidateEntityCache: () => {},
  refreshSelectedEntity: async () => {},
  fetchRelationshipsForSelected: async () => false,
  loadingProgress: null,
  // Backward compatibility
  selectedGeoEntity: null,
  setSelectedGeoEntity: () => {},
});

interface SelectionProviderProps {
  children: ReactNode;
}

const DEBOUNCE_MS = 100;

const SelectionProvider: React.FC<SelectionProviderProps> = ({ children }) => {
  const config = useConfig(); // Use proper config context
  const { profile, isInitialized } = useUser();
  const { enqueueSnackbar } = useSnackbar();
  
  // PERFORMANCE OPTIMIZATION: State management optimized to reduce cascading re-renders
  // - Using React.startTransition() to batch related state updates
  // - Memoized derived values and stable function references
  // - Reduced dependencies in useCallback to prevent function recreation
  
  // Legacy state (maintained for backward compatibility)
  const [filterArea, setFilterAreaInternal] = useState<string | null>(null);
  const [filterLayersVisible, setFilterLayersVisible] = useState(false);
  
  // New structured state - renamed to be more generic
  const [selectedPrimaryFilterEntity, setSelectedPrimaryFilterEntityInternal] = useState<Entity | null>(null);
  const [selectedEntity, setSelectedEntityInternal] = useState<Entity | null>(null);
  const [selectedEndpoint, setSelectedEndpointInternal] = useState<ApiEndpoint | null>(null);
  const [loadingProgress, setLoadingProgress] = useState<{
    step: string;
    current: number;
    total: number;
  } | null>(null);
  
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastUpdateRef = useRef<string | null>(null);

  // Add a ref to track entities currently being fetched to prevent race conditions
  const fetchingEntitiesRef = useRef<Set<string>>(new Set());
  
  // Add a ref to track active AbortControllers for cancelling outdated requests
  const activeControllersRef = useRef<Map<string, AbortController>>(new Map());

  // Log critical state changes for debugging
  useEffect(() => {
    if (selectedEntity) {
      console.log('[SelectionContext] Selected entity:', selectedEntity.name, `(${selectedEntity.entityType})`);
    }
  }, [selectedEntity]);



  // OPTIMIZED: Legacy setter with reduced state updates
  const setFilterArea = useCallback((value: string | null, showFilteredLayers: boolean = true) => {
    if (value === lastUpdateRef.current) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    lastUpdateRef.current = value;

    timeoutRef.current = setTimeout(() => {
      // BATCH: Update all related state together to reduce re-renders
      setFilterAreaInternal(value);
      setSelectedPrimaryFilterEntityInternal(value ? {
        id: value,
        name: value,
        entityType: 'familyArea'
      } : null);
      
      if (showFilteredLayers) {
        setFilterLayersVisible(true);
      }
    }, DEBOUNCE_MS);
  }, []); // Empty deps - function is stable

  // New structured setters
  const setSelectedPrimaryFilterEntity = useCallback((entity: Entity | null, showFilteredLayers: boolean = true) => {
    setSelectedPrimaryFilterEntityInternal(entity);
    setFilterAreaInternal(entity?.name ?? null);
    
    if (showFilteredLayers) {
      setFilterLayersVisible(true);
    }
  }, []);

  // Enhanced function to fetch complete entity data with adapter pattern support
  const fetchCompleteEntityData = useCallback(async (entity: Entity, endpoint: ApiEndpoint, options?: { includeRelationships?: boolean }): Promise<Entity | null> => {
    
    // DEBUG: Check user profile status
    if (process.env.NODE_ENV === 'development') {
      console.log('🚨 [FULCRUM-DEBUG] SelectionContext fetchCompleteEntityData user profile check:', {
        hasProfile: !!profile,
        isInitialized: isInitialized,
        profileKeyCount: profile ? Object.keys(profile).length : 0,
        hasFulcrumApiKey: !!(profile?.fulcrum_api_key),
        hasFulcrumApiKeyFlag: profile?.has_fulcrum_api_key,
        fulcrumRegion: profile?.fulcrum_region,
        timestamp: new Date().toISOString()
      });
    }
    
    // Early return if UserContext is not initialized yet
    if (!isInitialized) {
      console.warn('🚨 [FULCRUM-DEBUG] SelectionContext UserContext not initialized yet - waiting...');
      throw new Error('User context not initialized yet. Please wait a moment and try again.');
    }
    
    // Early return if no user profile is available (user not logged in)
    if (!profile) {
      console.error('🚨 [FULCRUM-DEBUG] SelectionContext no user profile available');
      enqueueSnackbar('User session lost. Please refresh the page to restore your session.', { 
        variant: 'warning',
        autoHideDuration: 6000
      });
      throw new Error('User session lost. Please refresh the page.');
    }
    
    // Create a unique key for this entity to prevent duplicate fetches
    const entityKey = `${entity.entityType}_${entity.id}`;
    
    // Cancel any existing request for this entity
    const existingController = activeControllersRef.current.get(entityKey);
    if (existingController) {
      existingController.abort();
      activeControllersRef.current.delete(entityKey);
      fetchingEntitiesRef.current.delete(entityKey);
    }
    
    // If we're already fetching this entity, return the current entity
    if (fetchingEntitiesRef.current.has(entityKey)) {
      return entity;
    }

    // Create new AbortController for this request
    const controller = new AbortController();
    activeControllersRef.current.set(entityKey, controller);

    // Mark this entity as being fetched
    fetchingEntitiesRef.current.add(entityKey);

    try {
      // Add timeout to prevent hanging requests (reduced to 15 seconds for better UX)
      const timeoutId = setTimeout(() => {
        console.warn('[SelectionContext] Request timed out for entity:', entity.id);
        enqueueSnackbar('Request timed out. Please check your internet connection.', { 
          variant: 'warning' 
        });
        setLoadingProgress(null);
        controller.abort();
      }, 15000); // 15 second timeout (longer to account for multiple queries)
      
      // Progress callback to track loading steps
      const onProgress = (step: string, current: number, total: number) => {
        setLoadingProgress({ step, current, total });
      };
      
      let completeData;
      try {
        // Use the generic adapter pattern instead of hardcoded Django endpoint
        // Pass all API endpoints for relationship resolution
        const allApiEndpoints = config.apiEndpoints || [];
        
        // Use the new 3-level fetch system
        if (options?.includeRelationships ?? true) {
          // LEVEL 3: Fetch with relationships
          console.log(`🔄 [SelectionContext] Using LEVEL 3 (with relationships) for:`, {
            entityId: entity.id,
            entityName: entity.name,
            endpointName: endpoint.name,
            dataSourceType: endpoint.dataSourceType
          });
          completeData = await fetchItemWithRelationships(
            entity.id,
            endpoint,
            profile,
            allApiEndpoints,
            onProgress
          );
        } else {
          // LEVEL 2: Fetch without relationships
          console.log(`🔄 [SelectionContext] Using LEVEL 2 (without relationships) for:`, {
            entityId: entity.id,
            entityName: entity.name,
            endpointName: endpoint.name,
            dataSourceType: endpoint.dataSourceType
          });
          const { fetchItem } = await import('../utilities/dataSourceAdapters');
          completeData = await fetchItem(
            entity.id,
            endpoint,
            profile,
            allApiEndpoints,
            onProgress
          );
        }
        clearTimeout(timeoutId);
        setLoadingProgress(null);
        
        // Check if request was cancelled while we were fetching
        if (controller.signal.aborted) {
          setLoadingProgress(null);
          return entity; // Return original entity if cancelled
        }
        
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        setLoadingProgress(null);
        
        if (fetchError instanceof Error && fetchError.name === 'AbortError') {
          return entity; // Return original entity instead of throwing
        }
        

        
        // Handle network errors gracefully - for critical network failures, return to grid
        if (fetchError.message?.includes('Failed to fetch') || 
            fetchError.message?.includes('ERR_INTERNET_DISCONNECTED') ||
            fetchError.message?.includes('Network Error')) {
          
          console.warn('[SelectionContext] Network error detected:', fetchError.message);
          
          // For severe network errors (like internet disconnected), clear selection and return to grid
          if (fetchError.message?.includes('ERR_INTERNET_DISCONNECTED')) {
            enqueueSnackbar('No internet connection. Returning to item list.', { 
              variant: 'error' 
            });
            
            // Clear selection to return to grid after a short delay to let user see the message
            setTimeout(() => {
              console.log('[SelectionContext] Clearing selection due to network disconnection');
              setSelectedEntityInternal(null);
              setSelectedEndpointInternal(null);
            }, 2000);
            
            // Return null to indicate failure
            return null;
          }
          
          // For other network errors, show warning but try to continue
          enqueueSnackbar('Network connection issue. Showing basic item details.', { 
            variant: 'warning' 
          });
          
          // Return the entity as-is (basic data from grid) rather than failing completely
          return {
            ...entity,
            isComplete: false,
            relationships: null
          };
        }
        
        // Handle "record not found" errors by returning to grid
        if (fetchError.message?.includes('not found') || 
            fetchError.message?.includes('404') ||
            fetchError.message?.includes('may have been deleted or moved')) {
          
          console.warn('[SelectionContext] Record not found, returning to grid:', fetchError.message);
          enqueueSnackbar('Record not found. It may have been deleted. Returning to item list.', { 
            variant: 'warning' 
          });
          
          // Clear selection to return to grid after a short delay
          setTimeout(() => {
            console.log('[SelectionContext] Clearing selection due to record not found');
            setSelectedEntityInternal(null);
            setSelectedEndpointInternal(null);
          }, 2000);
          
          // Return null to indicate failure
          return null;
        }
        
        // For other errors, show generic message and return basic entity
        console.error('[SelectionContext] Error fetching complete entity data:', fetchError);
        enqueueSnackbar('Unable to load full details. Showing basic information.', { 
          variant: 'info' 
        });
        
        return {
          ...entity,
          isComplete: false,
          relationships: null
        };
      }
      

      
      // Extract media from relationships - use generic approach that works with all data sources
      let relatedMedia: MediaItemDetailed[] = [];
      
      // Helper function to normalize media items regardless of source
      const normalizeMediaItem = (media: any): MediaItemDetailed => ({
        ...media,
        type: media.media_type || media.type || 'photo',
        media_type: media.media_type || media.type || 'photo',
        original_file: media.original_file || {
          filename: media.name || 'unknown',
          type: media.media_type || media.type || 'photo',
          size: '0'
        }
      });
      
      // Try multiple sources for media, using generic pattern matching
      // This approach is data-source agnostic and extensible
      
      // Helper function to find media objects in raw API data generically
      const findMediaInRawData = (data: any): any[] => {
        // Pattern 1: Look for fullMediaObjects in any nested data structure
        for (const key in data) {
          if (data[key] && typeof data[key] === 'object' && data[key].fullMediaObjects) {
            return data[key].fullMediaObjects;
          }
        }
        
        // Pattern 2: Direct relationships media (common pattern)
        if (data.relationships?.media && Array.isArray(data.relationships.media)) {
          return data.relationships.media;
        }
        
        // Pattern 3: Direct media array (fallback pattern)
        if (data.media && Array.isArray(data.media)) {
          return data.media;
        }
        
        return [];
      };
      
      const mediaItems = findMediaInRawData(completeData);
      if (mediaItems.length > 0) {
        relatedMedia = mediaItems.map(normalizeMediaItem);
      }

      // FUTURE: Once we have proper data structures, we can use:
      // const relatedMedia = getRelatedMedia(completeEntity); // After entity is built


      
              // Create complete entity with all data
        const completeEntity: Entity = {
          ...entity,
          // Update coordinates and geometry with fresh data if available
          coordinates: completeData.coordinates || entity.coordinates,
          geometry: completeData.geometry || entity.geometry,
          // Preserve important top-level fields from complete data (like thumbnail, name, description)
          name: completeData.name || entity.name,
          thumbnail: completeData.thumbnail || entity.thumbnail,
          description: completeData.description || entity.description,
          primary_image: completeData.primary_image || entity.primary_image,
          // Preserve all other important fields that might be used by UI components
          created_at: completeData.created_at || entity.created_at,
          last_updated_at: completeData.last_updated_at || entity.last_updated_at,
          url: completeData.url || entity.url, // For media items
          properties: {
            ...completeData,
            // Preserve source information
            ...(entity.properties || {}),
            // Ensure thumbnail is also available in properties for backward compatibility
            thumbnail: completeData.thumbnail || entity.thumbnail || entity.properties?.thumbnail,
            primary_image: completeData.primary_image || entity.primary_image || entity.properties?.primary_image
          },
          relationships: (options?.includeRelationships ?? true) ? completeData.relationships : undefined,
          relatedMedia,
          hasMedia: relatedMedia.length > 0,
          // Entity is complete if we have all the base data (Level 2), even without relationships
          isComplete: true
        };
      


      return completeEntity;

    } catch (error) {
      console.error('[SelectionContext] Error fetching complete entity data:', error);
      
      // Don't treat cancellation as an error
      if (error instanceof Error && error.name === 'AbortError') {
        return entity;
      }
      
      // For any other error, return entity with basic data to prevent UI breakage
      return entity;
    } finally {
      // Always clean up
      fetchingEntitiesRef.current.delete(entityKey);
      activeControllersRef.current.delete(entityKey);
    }
  }, [profile, isInitialized]);

  // OPTIMIZED: Memoized endpoint finder to reduce re-renders
  const findEndpointForEntity = useCallback((entity: Entity): ApiEndpoint | undefined => {
    // Enhanced endpoint resolution with multiple fallback strategies
    let targetEndpoint = config.apiEndpoints.find(
      (ep) => ep.tableName === entity.entityType ||
              ep.entityType === entity.entityType ||
              ep.mapLayerId === entity.layerId ||
              ep.tableName?.toLowerCase() === entity.entityType?.toLowerCase()
    );
    
    // Fallback: Try matching by name (case-insensitive)
    if (!targetEndpoint) {
      targetEndpoint = config.apiEndpoints.find(
        (ep) => ep.name.toLowerCase() === entity.entityType?.toLowerCase()
      );
    }
    
    // Special case for places
    if (!targetEndpoint && entity.entityType === 'places') {
      targetEndpoint = config.apiEndpoints.find(
        (ep) => ep.tableName.toLowerCase() === 'places' ||
                ep.name.toLowerCase().includes('place')
      );
    }
    
    return targetEndpoint;
  }, [config.apiEndpoints]);

  // OPTIMIZED: Enhanced setSelectedEntity with reduced re-renders  
  const setSelectedEntity = useCallback((entity: Entity | null, endpoint?: ApiEndpoint) => {
    
    console.log('🚨 [FULCRUM-DEBUG] SelectionContext setSelectedEntity called:', {
      entity: entity ? {
        id: entity.id,
        idType: typeof entity.id,
        name: entity.name,
        entityType: entity.entityType,
        layerId: entity.layerId,
        isComplete: entity.isComplete,
        hasProperties: !!entity.properties,
        propertiesKeys: entity.properties ? Object.keys(entity.properties) : []
      } : null,
      endpoint: endpoint ? {
        name: endpoint.name,
        tableName: endpoint.tableName,
        formId: endpoint.formId,
        dataSourceType: endpoint.dataSourceType
      } : null,
      timestamp: new Date().toISOString()
    });
    
    // Cancel all pending requests when switching to a new entity
    if (activeControllersRef.current.size > 0) {
      activeControllersRef.current.forEach((controller, key) => {
        controller.abort();
        fetchingEntitiesRef.current.delete(key);
      });
      activeControllersRef.current.clear();
    }
    
    // BATCH: Handle null entity (clearing selection) - update both states together
    if (!entity) {
      // Use function form to batch updates in React 18+
      React.startTransition(() => {
        setSelectedEntityInternal(null);
        
        if (endpoint === undefined) {
          setSelectedEndpointInternal(null);
        } else if (endpoint) {
          setSelectedEndpointInternal(endpoint);
        }
      });
      return;
    }
    
    // BATCH: Update entity and endpoint together to reduce re-renders
    React.startTransition(() => {
      // Update endpoint first if provided
      if (endpoint) {
        setSelectedEndpointInternal(endpoint);
      }
      
      // CRITICAL: Avoid data degradation - don't overwrite complete entities with incomplete ones
      setSelectedEntityInternal(prevEntity => {
        // If we already have a complete entity with the same ID, preserve it unless explicitly overriding
        if (prevEntity && 
            prevEntity.id === entity.id && 
            prevEntity.entityType === entity.entityType &&
            prevEntity.isComplete && 
            !entity.isComplete) {
          

          
          // Keep the complete entity but update any new properties that might have been added
          return {
            ...prevEntity,
            // Update name if the new entity has a better name
            name: entity.name && entity.name !== `Item ${entity.id}` ? entity.name : prevEntity.name,
            // Preserve other complete data
            isComplete: true,
            relationships: prevEntity.relationships,
            relatedMedia: prevEntity.relatedMedia,
            // Update layerId and other metadata that might be useful
            layerId: entity.layerId || prevEntity.layerId,
            coordinates: entity.coordinates || prevEntity.coordinates,
            geometry: entity.geometry || prevEntity.geometry,
            // Merge properties, preferring complete entity's data
            properties: {
              ...entity.properties,
              ...prevEntity.properties,
              // Keep source information
              fromDataItemDetails: entity.properties?.fromDataItemDetails || prevEntity.properties?.fromDataItemDetails
            }
          };
        }
        
        // Otherwise, use the new entity
        return entity;
      });
    });
    
    // If entity is already complete with base data, we're done (relationships can be fetched on demand)
    if (entity.isComplete) {
      return;
    }
    
    // ADAPTER PATTERN: Check if this is a non-fetchable Fulcrum photo
    // Enhanced detection to catch Fulcrum photos regardless of entityType
    const entityData = { ...entity, ...entity.properties };
    const dataSourceType = getDataSourceType(entityData, endpoint);
    
    const isFulcrumPhoto = !!(
      // Direct Fulcrum photo indicators
      (entityData as any).fulcrum_photo?.photo_id || 
      (entityData as any).fulcrum_data?.form_id || 
      (entityData as any)._source?.type === 'fulcrum' ||
      // Media type indicators combined with Fulcrum data source
      ((entityData as any).media_type === 'photo' && (
        (entityData as any).dataSourceType === 'fulcrum' ||
        (entityData as any).form_id ||
        (entityData as any).fulcrum_id ||
        dataSourceType === 'fulcrum'
      )) ||
      // Photo name pattern from Fulcrum (like "Photo 1", "Photo 10")
      (entity.name?.match(/^Photo \d+$/) && (
        (entityData as any).dataSourceType === 'fulcrum' ||
        endpoint?.dataSourceType === 'fulcrum' ||
        dataSourceType === 'fulcrum'
      ))
    );
    
    if (isFulcrumPhoto) {
      console.log('[SelectionContext] Preventing Fulcrum photo from being loaded as standalone entity:', {
        entityId: entity.id,
        entityName: entity.name,
        dataSourceType,
        entityType: entity.entityType,
        hasProperties: !!entity.properties,
        detectionReasons: {
          hasFulcrumPhoto: !!(entityData as any).fulcrum_photo?.photo_id,
          hasFulcrumData: !!(entityData as any).fulcrum_data?.form_id,
          hasSourceType: (entityData as any)._source?.type === 'fulcrum',
          hasMediaTypePhoto: (entityData as any).media_type === 'photo',
          hasDataSourceType: (entityData as any).dataSourceType === 'fulcrum',
          matchesPhotoPattern: !!entity.name?.match(/^Photo \d+$/),
          endpointIsFulcrum: endpoint?.dataSourceType === 'fulcrum'
        },
        reason: 'Fulcrum photos are attachments, not standalone fetchable records'
      });
      
      // Show user-friendly message and prevent the load
      enqueueSnackbar('This item does not have its own properties', { 
        variant: 'info' 
      });
      
      // Don't update the selection - this prevents the load entirely
      return;
    }
    
    // Find the appropriate endpoint for this entity using memoized finder
    let targetEndpoint = endpoint;
    if (!targetEndpoint) {
      targetEndpoint = findEndpointForEntity(entity);
    }
    
    if (!targetEndpoint) {
      console.warn('[SelectionContext] No endpoint found for entity type:', entity.entityType);
      return;
    }
    
    // Use setTimeout to make this async and avoid dependency issues
    setTimeout(async () => {
      try {
        console.log('🚨 [FULCRUM-DEBUG] SelectionContext about to fetch complete entity data:', {
          entityId: entity.id,
          entityIdType: typeof entity.id,
          entityName: entity.name,
          entityType: entity.entityType,
          targetEndpoint: targetEndpoint ? {
            name: targetEndpoint.name,
            tableName: targetEndpoint.tableName,
            formId: targetEndpoint.formId,
            dataSourceType: targetEndpoint.dataSourceType
          } : null,
          timestamp: new Date().toISOString()
        });
        
        // Check config setting to determine if we should always load relationships
        const includeRelationships = config.alwaysRequestRelatedItemsAndMedia ?? false;
        const levelDescription = includeRelationships ? 'LEVEL 3' : 'LEVEL 2';
        console.log(`🔄 [SelectionContext] setSelectedEntity triggering ${levelDescription} fetch for:`, {
          entityId: entity.id,
          entityName: entity.name,
          endpointName: targetEndpoint?.name,
          includeRelationships,
          configOverride: config.alwaysRequestRelatedItemsAndMedia,
          reason: includeRelationships ? 'Config: Always load relationships' : 'Default behavior - defer relationships'
        });
        const completeEntity = await fetchCompleteEntityData(entity, targetEndpoint!, { includeRelationships });
        
        console.log('🚨 [FULCRUM-DEBUG] SelectionContext fetchCompleteEntityData result:', {
          entityId: entity.id,
          hasCompleteEntity: !!completeEntity,
          isComplete: completeEntity?.isComplete,
          completeName: completeEntity?.name,
          timestamp: new Date().toISOString()
        });
        
        if (completeEntity && completeEntity.isComplete) {
          setSelectedEntityInternal(completeEntity);
        }
      } catch (error) {
        console.error('🚨 [FULCRUM-DEBUG] SelectionContext error in setTimeout fetch:', {
          entityId: entity.id,
          entityName: entity.name,
          error: error instanceof Error ? error.message : String(error),
          errorType: error instanceof Error ? error.constructor.name : typeof error,
          timestamp: new Date().toISOString()
        });
        
        if (!(error instanceof Error && error.name === 'AbortError')) {
          console.error('[SelectionContext] Failed to fetch complete data:', error);
          // Keep the basic entity even if complete fetch fails
        }
      }
    }, 0);
  }, [fetchCompleteEntityData, findEndpointForEntity]); // Add findEndpointForEntity to deps

  // Standalone endpoint setter
  const setSelectedEndpoint = useCallback((endpoint: ApiEndpoint | null) => {
    setSelectedEndpointInternal(endpoint);
  }, []);


  // OPTIMIZED: Helper function to update entity with media - batched updates
  const updateSelectedEntityWithMedia = useCallback((entity: Entity, mediaItems?: MediaItemDetailed[], endpoint?: ApiEndpoint) => {
    const entityWithMedia: Entity = {
      ...entity,
      relatedMedia: mediaItems || [],
      hasMedia: mediaItems ? mediaItems.length > 0 : false,
      isComplete: true // Mark as complete when media is added
    };
    

    
    // BATCH: Update entity and endpoint together to reduce re-renders
    React.startTransition(() => {
      setSelectedEntityInternal(entityWithMedia);
      
      // Update endpoint if provided
      if (endpoint) {
        setSelectedEndpointInternal(endpoint);
      }
    });
  }, []);

  // Current filter derived from primary filter entity
  const currentFilter = useMemo(() => ({
    type: "intersects" as FilterType,
    db_table: "FamilyArea",
    field: "name",
    value: filterArea || "",
  }), [filterArea]);

  // Create cleanup function for logout
  const cleanupSelectionContext = useCallback(() => {
    console.log('[SelectionContext] Cleaning up selection state on logout');
    
    // Clear all selected entities and state
    setSelectedEntityInternal(null);
    setSelectedPrimaryFilterEntityInternal(null);
    setSelectedEndpointInternal(null);
    setFilterAreaInternal(null);
    setLoadingProgress(null);
    
    // Cancel all pending requests
    if (activeControllersRef.current.size > 0) {
      activeControllersRef.current.forEach((controller) => {
        controller.abort();
      });
      activeControllersRef.current.clear();
      fetchingEntitiesRef.current.clear();
    }
    
    // Clear any timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    console.log('[SelectionContext] Selection state cleanup completed');
  }, []);

  // Register for logout cleanup
  useEffect(() => {
    registerContextCleanupHandler(cleanupSelectionContext);
    
    return () => {
      unregisterContextCleanupHandler(cleanupSelectionContext);
    };
  }, [cleanupSelectionContext]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      // Cancel all pending requests on unmount
      if (activeControllersRef.current.size > 0) {
        activeControllersRef.current.forEach((controller) => {
          controller.abort();
        });
        activeControllersRef.current.clear();
        fetchingEntitiesRef.current.clear();
      }
    };
  }, []);

  // Cache invalidation - mark entity as incomplete to force refetch
  const invalidateEntityCache = useCallback((entityId: string | number, entityType: string) => {
    // If this is the currently selected entity, mark it as incomplete
    if (selectedEntity && 
        selectedEntity.id === entityId && 
        selectedEntity.entityType === entityType) {
      const invalidatedEntity: Entity = {
        ...selectedEntity,
        isComplete: false,
        relationships: undefined, // Clear cached relationships
        relatedMedia: undefined   // Clear cached media
      };
      setSelectedEntityInternal(invalidatedEntity);
    }
  }, [selectedEntity]);

  // Refresh the currently selected entity by refetching its data
  const refreshSelectedEntity = useCallback(async () => {
    if (!selectedEntity || !selectedEndpoint) {
      return;
    }

    // First invalidate the cache
    invalidateEntityCache(selectedEntity.id, selectedEntity.entityType);
    
    // Then fetch fresh data
    const refreshedEntity = await fetchCompleteEntityData(selectedEntity, selectedEndpoint);
    
    if (refreshedEntity && refreshedEntity.isComplete) {
      setSelectedEntityInternal(refreshedEntity);
    } else {
      console.warn('[SelectionContext] Failed to refresh entity');
    }
  }, [selectedEntity, selectedEndpoint, invalidateEntityCache, fetchCompleteEntityData]);

  // Helper function to check if relationships contain meaningful items (respecting admin settings)
  const hasNonMediaRelationships = useCallback((relationships: any) => {
    if (!relationships) return false;
    
    // Check for related_entities
    const hasRelatedEntities = relationships.related_entities && 
      Object.values(relationships.related_entities).some((arr: any) => Array.isArray(arr) && arr.length > 0);
    
    // Check for geo_entities  
    const hasGeoEntities = relationships.geo_entities && 
      Object.values(relationships.geo_entities).some((arr: any) => Array.isArray(arr) && arr.length > 0);
    
    // Check for media only if admin setting allows it
    const hasMedia = config.showMediaInRelatedItemsCloud && 
      Array.isArray(relationships.media) && relationships.media.length > 0;
    
    return hasRelatedEntities || hasGeoEntities || hasMedia;
  }, [config.showMediaInRelatedItemsCloud]);

  // On-demand relationships fetch
  const fetchRelationshipsForSelected = useCallback(async () => {
    if (!selectedEntity || !selectedEndpoint) return false;
    try {
      console.log(`🔄 [SelectionContext] fetchRelationshipsForSelected triggering LEVEL 3 fetch for:`, {
        entityId: selectedEntity.id,
        entityName: selectedEntity.name,
        endpointName: selectedEndpoint.name,
        reason: 'On-demand relationships fetch'
      });
      
      // Check cache for related items first
      try {
        const { getCachedData } = await import('../utilities/relatedItemsCache');
        const cacheKey = `relationships_${selectedEntity.id}_${selectedEndpoint.name}`;
        const cachedRelationships = getCachedData<any>(cacheKey, 'relationships');
        
        if (cachedRelationships) {
          console.log(`🔄 [SelectionContext] Cache hit for relationships: ${selectedEntity.id}`);
          // Create a complete entity with cached relationships
          const entityWithCachedRels = {
            ...selectedEntity,
            isComplete: true,
            relationships: cachedRelationships
          };
          setSelectedEntityInternal(entityWithCachedRels);
          
          return hasNonMediaRelationships(cachedRelationships);
        } else {
          console.log(`🔄 [SelectionContext] Cache miss for relationships: ${selectedEntity.id}`);
        }
      } catch (cacheError) {
        console.warn('[SelectionContext] Cache check failed, proceeding with API fetch:', cacheError);
      }
      
      const full = await fetchCompleteEntityData(selectedEntity, selectedEndpoint, { includeRelationships: true });
      if (full && full.isComplete) {
        setSelectedEntityInternal(full);
        
        // Cache the relationships for future use
        if (full.relationships) {
          try {
            const { cacheData } = await import('../utilities/relatedItemsCache');
            const cacheKey = `relationships_${selectedEntity.id}_${selectedEndpoint.name}`;
            await cacheData(cacheKey, 'relationships', full.relationships);
            console.log(`🔄 [SelectionContext] Cached relationships for: ${selectedEntity.id}`);
          } catch (cacheError) {
            console.warn('[SelectionContext] Failed to cache relationships:', cacheError);
          }
        }
        
        return hasNonMediaRelationships(full.relationships);
      }
    } catch (e) {
      console.error('[SelectionContext] Error fetching relationships on demand:', e);
      
      // Handle specific "No user profile available" error with user-friendly message
      if (e instanceof Error && e.message?.includes('No user profile available')) {
        enqueueSnackbar('Unable to load relationships - please log in again.', { 
          variant: 'warning' 
        });
      } else if (e instanceof Error && e.message?.includes('not found')) {
        enqueueSnackbar('Record not found - it may have been deleted.', { 
          variant: 'warning' 
        });
      } else {
        enqueueSnackbar('Unable to load relationships at this time.', { 
          variant: 'info' 
        });
      }
    }
    return false;
  }, [selectedEntity, selectedEndpoint, fetchCompleteEntityData, enqueueSnackbar, hasNonMediaRelationships]);

  return (
    <SelectionContext.Provider
      value={{ 
        // Legacy values
        filterArea, 
        setFilterArea,
        currentFilter,
        filterLayersVisible,
        setFilterLayersVisible,
        
        // New structured values
        selectedPrimaryFilterEntity,
        setSelectedPrimaryFilterEntity,
        selectedEntity,
        setSelectedEntity,
        updateSelectedEntityWithMedia,
        
        // Endpoint tracking
        selectedEndpoint,
        setSelectedEndpoint,
        
        // Cache invalidation methods
        invalidateEntityCache,
        refreshSelectedEntity,
        fetchRelationshipsForSelected,
        
        // Loading progress
        loadingProgress,
        
        // Backward compatibility aliases
        selectedGeoEntity: selectedEntity,
        setSelectedGeoEntity: setSelectedEntity
      }}
    >
      {children}
    </SelectionContext.Provider>
  );
};

export { SelectionContext, SelectionProvider };
